import {FlexPage} from '@hconnect/common/components/FlexPage'
import {
  noSideCard,
  sideCardSize,
  tableWithOutSideCard,
  tableWithSideCard,
  withSideCard
} from '@hconnect/common/consts'
import {useWidth} from '@hconnect/common/hooks/useWidth'
import {useQueryParamValue} from '@hconnect/common/query-params'
import {FilterContrastProvider} from '@hconnect/common/styles'
import {EventsTab, SortOrder} from '@hconnect/common/types'
import {CardBox, DataTable, type PaginationOptions} from '@hconnect/uikit/src/lib2'
import {useCommonFeatureFlag, FeatureFlagKey} from '@hproduce/feature-flags'
import {Grid, Theme} from '@mui/material'
import {FilterOptions} from '@widgets/shift-event-filters'
import {TFunction} from 'i18next'
import React, {ReactNode, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {ShiftEventsTabToggle} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {useInvertedSurface} from '../context/InvertedSurfaceContext'
import {eventRowSx} from '../styles/eventTable'
import {ColumnsMap, SideCardComponentProps} from '../types'
import {getSortDirection, getSortOrder} from '../utils'

type EventsListViewProps<T, L> = {
  activeTab: EventsTab
  setActiveTab: (tab: EventsTab) => void
  activeFilter: FilterOptions
  events?: T[]
  isLoading?: boolean
  showBottomProgress?: boolean
  paginationOptions?: PaginationOptions
  generateColumnsMap: (t: TFunction, freeSearchText?: string) => ColumnsMap<T>
  SideCardComponent: SideCardComponentProps<T>
  urlParamId: string
  sortedBy?: L
  sortDirection?: SortOrder
  onSort: (columnName: string, sortOrder?: SortOrder) => void
  plantId: string
  headerActionContent: ReactNode
}

export const EventsListView = <T extends {id: string}, L extends string>({
  activeTab,
  setActiveTab,
  activeFilter,
  events,
  isLoading,
  paginationOptions,
  generateColumnsMap,
  SideCardComponent,
  urlParamId,
  sortedBy,
  sortDirection,
  onSort,
  plantId,
  headerActionContent
}: EventsListViewProps<T, L>) => {
  const breakPoint = useWidth()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const inverted = useInvertedSurface()
  const keys = useMemo(() => [urlParamId], [urlParamId])
  const [selectedItemId = '', setSelectedItemId] = useQueryParamValue(urlParamId, keys, {})
  const activeItemId = new URLSearchParams(selectedItemId).get(urlParamId) ?? undefined
  const selectedItem = useMemo(
    () => events?.find((item) => item.id === activeItemId),
    [activeItemId, events]
  )
  const columns = generateColumnsMap(t, activeFilter.freeText)
  const tableSize = (activeItemId ? withSideCard : noSideCard).get(breakPoint) ?? 'S'
  const isSmall = breakPoint === 'xs' || breakPoint === 'sm'
  const isTableShown = !activeItemId || !isSmall
  const currentSortDirection = getSortDirection(sortDirection)
  const hideTabsWorkOrderNotification = useCommonFeatureFlag(
    FeatureFlagKey.HideWorkOrderNotification,
    {plantId}
  )

  return (
    <FlexPage
      appName="Cockpit"
      title={
        <FilterContrastProvider isContrastMode={inverted}>
          <ShiftEventsTabToggle
            plantId={plantId}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </FilterContrastProvider>
      }
      headerActionContent={
        <FilterContrastProvider isContrastMode={inverted}>
          <Grid container alignItems="center">
            {isTableShown && (
              <Grid item xs={12}>
                {headerActionContent}
              </Grid>
            )}
          </Grid>
        </FilterContrastProvider>
      }
      useViewportHeight={!isSmall}
    >
      {hideTabsWorkOrderNotification && (
        <Grid container flexGrow={1} overflow={'hidden'} spacing={2}>
          {isTableShown && (
            <Grid
              item
              height={'100%'}
              {...(activeItemId ? tableWithSideCard : tableWithOutSideCard)}
            >
              <CardBox
                sx={(theme: Theme) => ({
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  [theme.breakpoints.between('md', 'xl')]: {
                    zoom: 0.8
                  }
                })}
              >
                <DataTable<T>
                  columns={columns[tableSize]}
                  data={events || []}
                  rowSx={(item) => eventRowSx(activeItemId === item.id)}
                  emptyMessage={(!isLoading && t('eventsTable.noData')) ?? ''}
                  onRowClick={(keyEvent: React.MouseEvent, clickedItem: T) =>
                    setSelectedItemId(`${urlParamId}=${clickedItem.id}`)
                  }
                  paginationOptions={paginationOptions}
                  loading={isLoading}
                  data-test-id="events-table-container"
                  sortedBy={sortedBy}
                  sortDirection={currentSortDirection}
                  onSort={(event, columnKey) => {
                    const sortOrder = getSortOrder(
                      sortedBy && currentSortDirection === 'asc' ? 'desc' : 'asc'
                    )
                    onSort(columnKey, sortOrder)
                  }}
                />
              </CardBox>
            </Grid>
          )}

          {activeItemId && (
            <Grid
              item
              {...sideCardSize}
              sx={{
                height: '100%',
                overflow: 'hidden'
              }}
            >
              <SideCardComponent
                item={selectedItem}
                itemId={activeItemId}
                doClose={() => setSelectedItemId('')}
                plantId={plantId}
              />
            </Grid>
          )}
        </Grid>
      )}
    </FlexPage>
  )
}
